import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../system/admin-access";
import {fetchAllAdminCollection} from "../../system/content-type-admin-api";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {useTranslate} from "../../system/useTranslate";
import {parseDate} from "../../system/utils";
import {
    buildWorkspaceHealth,
    entityId,
    entityLabel,
    groupBy,
    isCurrentSubscription,
    workspaceIdOf,
} from "../operations/operations-utils";

function errorText(error) {
    return error?.response?.data?.error?.message || error?.message || "workspaces-load-error";
}

function statusSeverity(status) {
    if (["active", "healthy"].includes(status)) return "success";
    if (["suspended", "archived", "critical"].includes(status)) return "danger";
    return "warning";
}

function OperationsStat({label, value, severity}) {
    const translate = useTranslate();
    return (
        <div className="col-12 md:col-6 xl:col-3">
            <div className="surface-card border-1 border-200 border-round p-3 flex align-items-center justify-content-between gap-3 h-full">
                <span className="text-sm text-600">{translate(label)}</span>
                <Tag value={value} severity={severity}/>
            </div>
        </div>
    );
}

function Index() {
    const navigate = useNavigate();
    const translate = useTranslate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [healthFilter, setHealthFilter] = useState(null);
    const adminUser = useSelector((state) => state.auth.user);

    const loadWorkspaces = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const requests = await Promise.allSettled([
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER_ROLE, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.SUBSCRIPTION, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.CREDIT_WALLET, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING, {pageSize: 200}),
            ]);
            const items = (index) => requests[index].status === "fulfilled"
                ? requests[index].value.items
                : [];
            if (requests[0].status === "rejected") throw requests[0].reason;

            const subscriptions = items(3);
            const subscriptionsByWorkspace = groupBy(subscriptions, workspaceIdOf);
            const healthRows = buildWorkspaceHealth({
                workspaces: items(0),
                memberships: items(1),
                roles: items(2),
                subscriptions,
                wallets: items(4),
                aiSettings: items(5),
            });
            setRows(healthRows.map((workspace) => {
                const currentSubscription = (
                    subscriptionsByWorkspace.get(String(entityId(workspace))) || []
                ).find((subscription) => isCurrentSubscription(subscription));
                return {
                    ...workspace,
                    _owner: entityLabel(workspace.owner),
                    _status: workspace.workspaceStatus || "active",
                    _subscription: currentSubscription
                        ? entityLabel(currentSubscription.plan) || currentSubscription.subscriptionStatus
                        : "-",
                };
            }));
        } catch (requestError) {
            setError(errorText(requestError));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const canViewWorkspaces = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.WORKSPACE_VIEW);

    useEffect(() => {
        if (canViewWorkspaces) loadWorkspaces();
    }, [canViewWorkspaces, loadWorkspaces]);

    const visibleRows = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        return rows.filter((row) => {
            if (healthFilter && row._health !== healthFilter) return false;
            if (!normalizedQuery) return true;
            return [
                row.name,
                row.documentId,
                row._owner,
                row.workspaceCategory,
                row._status,
            ].some((value) => String(value || "").toLowerCase().includes(normalizedQuery));
        });
    }, [healthFilter, query, rows]);

    const healthyCount = rows.filter((row) => row._health === "healthy").length;
    const criticalCount = rows.filter((row) => row._health === "critical").length;
    const suspendedCount = rows.filter(
        (row) => ["suspended", "readonly", "archived"].includes(row._status)
    ).length;

    if (!canViewWorkspaces) {
        return <AdminAccessDenied/>;
    }

    return (
        <div className="w-full h-full overflow-hidden flex flex-column gap-3 p-3">
            <PageHeaderButton
                title="workspaces"
                description={translate("workspaces-operations-description")}
                end={(
                    <Button
                        icon="pi pi-refresh"
                        label={translate("refresh")}
                        className="p-button-sm p-button-outlined gap-2"
                        loading={loading}
                        onClick={loadWorkspaces}
                    />
                )}
            />

            {error && <div className="text-red-500 text-sm px-3">{translate(error)}</div>}

            <div className="grid m-0">
                <OperationsStat label={translate("total-workspaces")} value={rows.length} severity="info"/>
                <OperationsStat label={translate("workspace-health-healthy")} value={healthyCount} severity="success"/>
                <OperationsStat label={translate("workspace-health-critical")} value={criticalCount} severity="danger"/>
                <OperationsStat label={translate("workspace-restricted-count")} value={suspendedCount} severity="warning"/>
            </div>

            <div className="surface-card border-1 border-200 border-round p-3 flex flex-wrap align-items-center gap-2">
                <span className="p-input-icon-right flex-1 min-w-16rem">
                    <i className="pi pi-search"/>
                    <InputText
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={translate("workspace-search-placeholder")}
                        className="w-full"
                    />
                </span>
                <Dropdown
                    value={healthFilter}
                    onChange={(event) => setHealthFilter(event.value)}
                    options={["healthy", "warning", "critical"].map((value) => ({
                        value,
                        label: translate(`workspace-health-${value}`),
                    }))}
                    placeholder={translate("workspace-health-filter")}
                    showClear
                    className="min-w-12rem"
                />
            </div>

            <div className="surface-card border-1 border-200 border-round flex-1 overflow-hidden">
                <DataTable
                    value={visibleRows}
                    dataKey="_key"
                    loading={loading}
                    scrollable
                    scrollHeight="flex"
                    rowHover
                    emptyMessage={translate("no-records")}
                    onRowClick={(event) => navigate(`/workspaces/${event.data.documentId || event.data.id}`)}
                    rowClassName="cursor-pointer"
                    className="h-full"
                    tableClassName="text-sm"
                >
                    <Column
                        header={translate("workspace-name")}
                        body={(row) => (
                            <div className="flex flex-column gap-1">
                                <span className="font-medium text-900">{row.name || "-"}</span>
                                <span className="text-xs text-500 ltr">{row.documentId || row.id}</span>
                            </div>
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column field="_owner" header={translate("workspace-owner")} bodyClassName="text-right" headerClassName="text-right"/>
                    <Column
                        header={translate("status")}
                        body={(row) => <Tag value={translate(`workspace-status-${row._status}`)} severity={statusSeverity(row._status)}/>}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column field="_memberships" header={translate("members-count")} bodyClassName="text-right ltr" headerClassName="text-right"/>
                    <Column field="_subscription" header={translate("subscription")} bodyClassName="text-right" headerClassName="text-right"/>
                    <Column
                        header={translate("workspace-health")}
                        body={(row) => (
                            <div className="flex align-items-center gap-2">
                                <Tag
                                    value={translate(`workspace-health-${row._health}`)}
                                    severity={statusSeverity(row._health)}
                                />
                                <span className="text-xs text-500 ltr">
                                    {row._healthScore}% · {row._issues.length} {translate("issues")}
                                </span>
                            </div>
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column
                        header={translate("updatedAt")}
                        body={(row) => row.updatedAt
                            ? parseDate(row.updatedAt, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en")
                            : "-"}
                        bodyClassName="text-right ltr"
                        headerClassName="text-right"
                    />
                    <Column
                        header={translate("actions")}
                        body={(row) => (
                            <Button
                                icon="pi pi-arrow-left"
                                label={translate("workspace-open-operations")}
                                className="p-button-sm p-button-text gap-2"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/workspaces/${row.documentId || row.id}`);
                                }}
                            />
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                </DataTable>
            </div>
        </div>
    );
}

export default Index;
